import { useContext, useEffect } from 'react';

import footer_en from '@Translations/en/pages/footer.json';
import loan_preferences_en from '@Translations/en/pages/loanPreferences.json';
import axios from 'axios';
import i18next from 'i18next';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import ApplicationContextProvider from '@App/contexts/Application';
import AuthProvider, { AuthContext } from '@App/contexts/Auth';

import DebtConContextProvider from '../contexts/DebtCon';
import OfferContextProvider from '../contexts/Offer';
import { KameleeonProvider } from '../utils/kameleoon';

i18next.init({
    interpolation: { escapeValue: false }, // React already does escaping
    lng: 'en',
    resources: {
        en: {
            footer: footer_en,
            loanPreferences: loan_preferences_en,
        },
    },
});

const AuthFrame = (props: { children: any }) => {
    const { getAccessToken, isToFetchApplicationData, getIdToken } =
        useContext(AuthContext);

    return (
        <Router>
            <ApplicationContextProvider
                getAccessToken={getAccessToken}
                isToFetchApplication={isToFetchApplicationData}
                getIdToken={getIdToken}
            >
                <DebtConContextProvider
                    getAccessToken={getAccessToken}
                    getIdToken={getIdToken}
                >
                    <OfferContextProvider
                        getAccessToken={getAccessToken}
                        getIdToken={getIdToken}
                    >
                        <I18nextProvider i18n={i18next}>
                            {props.children}
                        </I18nextProvider>
                    </OfferContextProvider>
                </DebtConContextProvider>
            </ApplicationContextProvider>
        </Router>
    );
};

const Providers = (props: { children: any }) => {
    return (
        <AuthProvider>
            <KameleeonProvider>
                <AuthFrame>
                    <AxiosInterceptor>{props.children}</AxiosInterceptor>
                </AuthFrame>
            </KameleeonProvider>
        </AuthProvider>
    );
};

const axiosInstance = axios.create();

const AxiosInterceptor = (props: any) => {
    const navigate = useNavigate();
    const { logOut } = useContext(AuthContext);

    useEffect(() => {
        const requestInterceptor = axiosInstance.interceptors.request.use(
            (config) => {
                config.headers = config.headers || {};

                config.headers['x-selina-trace-id'] = uuidv4();
                config.headers['x-selina-caller-id'] = 'MS-D2C';

                return config;
            },
            (error) => Promise.reject(error),
        );

        const responseInterceptor = axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (
                    error.response?.status === 401 ||
                    error.response?.status === 403
                ) {
                    logOut();
                    navigate('/login');
                }
                return Promise.reject(error);
            },
        );

        return () => {
            axiosInstance.interceptors.request.eject(requestInterceptor);
            axiosInstance.interceptors.response.eject(responseInterceptor);
        };
    });

    return <>{props.children}</>;
};

export { axiosInstance };
export default Providers;
